import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useLayoutEffect, useRef, useState } from 'react';

import { BILLING_PAYMENT_METHODS_QUERY_KEY } from '@/lib/query/constants/subscription-keys-constants';

import { apiErrorLogger, apiInfoLogger } from '@/lib/monitoring/api-logger';
import { useAccountState } from '@/contexts/account/account-context';
import { SubscriptionsApiService } from '@/services/internal-api/subscriptions/subscriptions-api-service';
import type { CompleteStripeSetupIntentResponse } from '@/services/internal-api/subscriptions/types/subscription-api-service-types';
import * as Logger from '@/lib/monitoring/logger';

const PAYMENT_METHOD_LIST_SYNC_DELAY_MS = 1500;

interface CompleteSetupIntentOptions {
  makeDefaultPaymentMethod?: boolean;
}

interface UseAddPaymentMethodReturn {
  clientSecret: string | null;
  setupIntentId: string | null;
  isCreatingSetupIntent: boolean;
  createSetupIntentError: string | null;
  createSetupIntent: () => Promise<void>;
  completeSetupIntent: (paymentMethodId: string, options?: CompleteSetupIntentOptions) => Promise<CompleteStripeSetupIntentResponse>;
  isCompletingSetupIntent: boolean;
  completeSetupIntentError: string | null;
  reset: () => void;
}

export function useAddPaymentMethod(): UseAddPaymentMethodReturn {
  const queryClient = useQueryClient();
  const { currentAccount } = useAccountState();

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupIntentId, setSetupIntentId] = useState<string | null>(null);
  const [createSetupIntentError, setCreateSetupIntentError] = useState<string | null>(null);

  // Ref to hold setupIntentId for stable access in mutation closures
  const setupIntentIdRef = useRef<string | null>(null);

  // Ref to hold current account ID for stable access in mutation closures
  const accountIdRef = useRef<string | null>(currentAccount?.id ?? null);
  useLayoutEffect(() => {
    accountIdRef.current = currentAccount?.id ?? null;
  });

  const createSetupIntentMutation = useMutation({
    mutationFn: async () => {
      const accountId = accountIdRef.current;
      if (!accountId) {
        throw new Error('Account ID is required');
      }
      return SubscriptionsApiService.createStripeSetupIntent({ accountId });
    },
    mutationKey: ['billing-create-setup-intent'],
    onSuccess: res => {
      apiInfoLogger('Create setup intent successful', 'useAddPaymentMethod', res);
      const { setupIntentClientSecret, setupIntentId: id } = res.data;
      setClientSecret(setupIntentClientSecret);
      setSetupIntentId(id);
      setupIntentIdRef.current = id;
      setCreateSetupIntentError(null);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      apiErrorLogger('Create setup intent failed', 'useAddPaymentMethod', error);
      const message =
        error.response?.data?.message ?? error.message ?? 'Failed to initialize payment form';
      setCreateSetupIntentError(message);
    },
  });

  const completeSetupIntentMutation = useMutation({
    mutationFn: async ({ paymentMethodId, makeDefaultPaymentMethod }: { paymentMethodId: string; makeDefaultPaymentMethod?: boolean }) => {
      const intentId = setupIntentIdRef.current;
      if (!intentId) {
        throw new Error('Setup intent ID is required');
      }
      const accountId = accountIdRef.current;
      if (!accountId) {
        throw new Error('Account ID is required');
      }
      return SubscriptionsApiService.completeStripeSetupIntent(intentId, {
        accountId,
        paymentMethodId,
        ...(makeDefaultPaymentMethod !== undefined && { makeDefaultPaymentMethod }),
      });
    },
    mutationKey: ['billing-complete-setup-intent'],
    onSuccess: res => {
      apiInfoLogger('Complete setup intent successful', 'useAddPaymentMethod', res);

      // Stripe's payment_methods list endpoint has a brief read-after-write window where
      // the newly-attached card may not yet appear. The completeSetupIntent response does not
      // include the new PaymentMethod object, so we can't write the canonical post-mutation
      // state directly. Instead, delay the invalidation so the eventual refetch sees the new
      // card; this avoids the "added card disappears" stale-cache race.
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: [BILLING_PAYMENT_METHODS_QUERY_KEY] });
      }, PAYMENT_METHOD_LIST_SYNC_DELAY_MS);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      apiErrorLogger('Complete setup intent failed', 'useAddPaymentMethod', error);
    },
  });

  const createSetupIntent = async () => {
    setCreateSetupIntentError(null);
    try {
      await createSetupIntentMutation.mutateAsync();
    } catch (error) {
      Logger.debug('Create setup intent failed; handled by onError', { error });
      // Error already handled in onError callback
    }
  };

  const completeSetupIntent = async (
    paymentMethodId: string,
    options?: CompleteSetupIntentOptions
  ): Promise<CompleteStripeSetupIntentResponse> => {
    const result = await completeSetupIntentMutation.mutateAsync({
      paymentMethodId,
      makeDefaultPaymentMethod: options?.makeDefaultPaymentMethod,
    });
    return result.data;
  };

  const reset = () => {
    setClientSecret(null);
    setSetupIntentId(null);
    setupIntentIdRef.current = null;
    setCreateSetupIntentError(null);
    createSetupIntentMutation.reset();
    completeSetupIntentMutation.reset();
  };

  return {
    clientSecret,
    setupIntentId,
    isCreatingSetupIntent: createSetupIntentMutation.isLoading,
    createSetupIntentError,
    createSetupIntent,
    completeSetupIntent,
    isCompletingSetupIntent: completeSetupIntentMutation.isLoading,
    completeSetupIntentError:
      completeSetupIntentMutation.error?.response?.data?.message ??
      completeSetupIntentMutation.error?.message ??
      null,
    reset,
  };
}
